import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SimpleTooltip } from '@/components/overlays/simple-tooltip';
import { cn } from '@/lib/core/utils';
import { getEmployeeAvatarColor, getEmployeeInitials } from '@/lib/employees/avatar';

const SIZE_PRESETS = {
  xs:  { container: 'h-6 w-6',  text: 'text-2xs font-medium' },
  sm:  { container: 'h-8 w-8',  text: 'text-xs font-medium' },
  md:  { container: 'h-9 w-9',  text: 'text-xs font-medium' },
  default: { container: 'h-10 w-10', text: 'text-sm font-medium' },
  lg:  { container: 'h-12 w-12', text: 'text-sm font-medium' },
  xl:  { container: 'h-14 w-14', text: 'text-xl font-medium' },
  '2xl': { container: 'h-16 w-16', text: 'text-xl font-semibold' },
} as const;

export type EmployeeAvatarSize = keyof typeof SIZE_PRESETS;

interface EmployeeAvatarProps {
  name: string;
  size?: EmployeeAvatarSize;
  imageUrl?: string;
  colorOverride?: string;
  className?: string;
  /** Show name tooltip on hover (default: true) */
  showTooltip?: boolean;
}

export const EmployeeAvatar: React.FC<EmployeeAvatarProps> = ({
  name,
  size = 'default',
  imageUrl,
  colorOverride,
  className,
  showTooltip = true,
}) => {
  const preset = SIZE_PRESETS[size];
  const bgColor = colorOverride || getEmployeeAvatarColor(name);
  const initials = getEmployeeInitials(name);

  const avatar = (
    <Avatar className={cn(preset.container, className)}>
      {imageUrl && <AvatarImage src={imageUrl} alt={name} />}
      <AvatarFallback className={cn('text-white', preset.text, bgColor)}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  if (showTooltip && name) {
    return <SimpleTooltip content={name}>{avatar}</SimpleTooltip>;
  }

  return avatar;
};

export default EmployeeAvatar;
